import { memo, useCallback } from 'react';
import isEqual from 'react-fast-compare';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

import TimedOrCategorySearchCard from '@/components/GridItemCard/Card/MultiCardOptions/TimedOrCategorySearchCard/TimedOrCategorySearchCard';
import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import useGetSearchColumns from '@/hooks/useGetSearchColumns';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { trackClickForAlgolia, trackPromotedItemVisible } from '@/redux/modules/analytics';
import { getBaseUrl } from '@/redux/modules/config';
import { getLandingPageMetaData } from '@/redux/modules/landingPage';
import { getSearchPageSize } from '@/redux/modules/pagination';
import { getSearchedAndPromotedItems } from '@/redux/modules/search/aggregateResults/searchAggregate.selectors';
import { getSearchUiState } from '@/redux/modules/search/search.selectors';
import { GlobalState } from '@/redux/store';
import { getPageFromQueryParams, parseQueryParams } from '@/utils/queryParams';
import { isSimilarItemsPage } from '@/utils/urls';

import FluidItemGrid from '../GridItemCard/FluidItemGrid/FluidItemGrid';
import { PAGE_ITEMS_TO_VIEW } from '../Pagination/helpers/filterSortOptions';
import { breakpoints } from './constants/SearchResults.constants';

type RouteParams = {
    categoryId: string;
    categoryId2: string;
};

type Props = {
    changePageAndNavigate: Function;
    changePageSizeAndNavigate: Function;
    trackItemClick?: Function;
};

const SearchResults = ({ changePageAndNavigate, changePageSizeAndNavigate }: Props) => {
    const dispatch = useAppDispatch();
    const { pathname, search } = useLocation();
    const pathAndQuery = pathname + search;
    const params = useParams<RouteParams>();
    const configBaseUrl = useAppSelector(getBaseUrl);
    const baseUrl = (typeof window !== 'undefined' && window?.location?.origin) || configBaseUrl;
    const queryParams = parseQueryParams(search);
    const page = getPageFromQueryParams(queryParams);
    const pageSize = useAppSelector(getSearchPageSize);
    const { totalFound, totalPages } = useAppSelector(getSearchUiState);
    const similarItemsPage = isSimilarItemsPage(pathname);
    const landingPageMetaData = useAppSelector((state: GlobalState) =>
        getLandingPageMetaData(state, params, pathname, queryParams)
    );

    const columns = useGetSearchColumns();
    const searchPageItemIds = useAppSelector((state: GlobalState) => getSearchedAndPromotedItems(state, columns));

    let canonicalURL = landingPageMetaData?.canonicalURL ? landingPageMetaData.canonicalURL : pathAndQuery;

    if (similarItemsPage) {
        canonicalURL = canonicalURL.replace(/\/(c|search)\//, '/similar-items/');
    } else if (pathname.includes('/price-guide/')) {
        canonicalURL = canonicalURL.replace(/\/(c)\//, '/price-guide/');
    }
    canonicalURL = `${baseUrl}${canonicalURL}`;

    const trackClick = useCallback(
        (itemId, position) => {
            dispatch(trackClickForAlgolia(itemId, position));
        },
        [dispatch]
    );

    const trackVisible = useCallback(
        (itemId: number, promoted: boolean) => {
            if (promoted) {
                dispatch(trackPromotedItemVisible({ itemId }));
            }
        },
        [dispatch]
    );

    const onChange = ({ page, pageSize }) => {
        if (!pageSize) {
            changePageAndNavigate(page - 1);
        } else {
            changePageSizeAndNavigate(pageSize);
        }
    };

    return (
        <StyledResults>
            <Pagination
                canonicalUrl={canonicalURL}
                onChange={onChange}
                pageSizeOptions={PAGE_ITEMS_TO_VIEW}
                paginationFilter={{ page, pageSize, totalRecords: totalFound }}
                restrictPagesTo={10}
                totalPages={totalPages}
            >
                <FluidItemGrid
                    breakpoints={breakpoints}
                    itemCardOption={TimedOrCategorySearchCard}
                    itemIds={searchPageItemIds}
                    lazyLoadImage={false}
                    page={page}
                    pageSize={pageSize}
                    trackClick={trackClick}
                    trackVisible={trackVisible}
                />
            </Pagination>
        </StyledResults>
    );
};

export default memo<Props>(SearchResults, isEqual);

const StyledResults = styled.div`
    flex: 1 1;
    position: relative;
`;
